/**
 * This is a Server component. Should only be used in app-router pages
 */

import Link from "next/link";
import { ISubscriptionStore, SubscriptionATStore } from "/lib/subscription";
import { cn } from "/lib/utils";
import { createLookupTable } from "/lib";

export type PriceElement = {
  name: string;
  salesPoints: string[];
  price: number;
  primary: boolean;
  showCta: boolean;
  ctaButtonText?: string;
  ctaLink?: string;
};

type PriceData = {
  subscriptionProductIds: string[];
  bikeModelId: string;
};

/**
 * @param comparisonKey: Lookup key in `objectData`
 * @param objectData: Object list to sort
 * @param inputArr: Input array used to sort object list
 * @returns object list sorted according to index order of input array
 */
function sortAccordingToInputArray<K extends keyof V, V extends { [Key in K]: string }>(
  comparisonKey: K,
  objectData: V[],
  inputArr: string[]
): V[] {
  return objectData
    .slice()
    .sort((a, b) => inputArr.indexOf(a[comparisonKey]) - inputArr.indexOf(b[comparisonKey]));
}

async function getData({ subscriptionProductIds, bikeModelId }: PriceData) {
  const subscriptionStore: ISubscriptionStore = new SubscriptionATStore();

  const subscriptionData = await subscriptionStore.getSubscriptionProductsByBikeModelId(
    bikeModelId
  );

  return sortAccordingToInputArray(
    "subscriptionProductId",
    subscriptionData,
    subscriptionProductIds
  );
}

type PriceElementData = Omit<
  PriceElement,
  "salesPoints" | "price" | "subscriptionId" | "bikeModelId"
> & {
  subscriptionProductId: string;
};

export default async function Price({
  bikeModelId,
  elementData,
  stacked = false
}: {
  bikeModelId?: string;
  elementData: PriceElementData[];
  stacked?: boolean;
}) {
  const subscriptionProductIds = elementData.map((el) => el.subscriptionProductId);

  // Creates lookup table to fetch element props by subscriptionProductId
  const elementPropsForSubscription: Record<string, PriceElementData> = createLookupTable(
    elementData,
    "subscriptionProductId"
  );

  if (bikeModelId) {
    const subscriptionData = await getData({ subscriptionProductIds, bikeModelId });

    const modifiedElementData = subscriptionData.map((data) => {
      const element: PriceElementData = elementPropsForSubscription[data.subscriptionProductId];

      let ctaLink: string | undefined = undefined;

      if (data.subscriptionId !== null) {
        ctaLink = `/sykler/bestill/${bikeModelId}?subscriptionId=${data.subscriptionId}`;
      }

      return {
        ...element,
        ...data,
        ctaLink: ctaLink
      };
    });

    return (
      <article className="w-full flex gap-4 justify-center">
        {modifiedElementData.map((element, i) => {
          return <Element key={i} data={element} />;
        })}
      </article>
    );
  } else {
    // This could be moved to the CMS, and rather pick up relevant subscription product data from Airtable
    const fixedData = [
      {
        name: "Whee! Fleks",
        primary: false,
        showCta: false,
        salesPoints: ["Ingen binding", "3 mnd oppsigelse", "Frihet til å velge"],
        price: 1800
      },
      {
        name: "Whee! Fast",

        primary: true,
        showCta: false,
        salesPoints: ["12 mnd binding", "deretter 3 mnd løpende oppsigelse", "Vår beste deal"],
        price: 1400
      }
    ];

    return (
      <article className="w-full flex gap-4 overflow-x-auto scrollbar-hide h-full pb-2 justify-center">
        {fixedData.map((element, i) => {
          return <Element key={i} data={element} />;
        })}
      </article>
    );
  }
}

export const Element = ({ data }: { data: PriceElement }) => {
  const elementConfig = {
    primary: {
      bgColor: `bg-[#fff1e5]`
    },
    secondary: {
      bgColor: `bg-[#fff9f1]`
    }
  };

  return (
    <div
      className={cn(
        `flex flex-col w-full rounded-3xl p-8 pb-4 shadow-md ${elementConfig.secondary.bgColor} max-w-[440px] justify-evenly`,
        data.primary && elementConfig.primary.bgColor
      )}
    >
      <div>
        <h3 className="font-medium text-[36px] leading-[134%]">{data.name}</h3>
        <hr
          className={cn(
            "h-[2px]",
            elementConfig.primary.bgColor,
            data.primary && elementConfig.secondary.bgColor
          )}
        />
      </div>
      <div className="flex flex-col gap-3 mt-6">
        {data.salesPoints.map((point, i) => {
          return (
            <p key={i} className="font-medium text-[18px] leading-5">
              {point}
            </p>
          );
        })}
      </div>
      <div className="w-full flex flex-col md:flex-row md:justify-end mt-8">
        {data.showCta && data.ctaLink && (
          <span className="md:mr-auto self-center">
            <Link
              href={data.ctaLink}
              className="mr-auto px-5 py-3 rounded-full bg-green text-cream text-[18px] font-medium leading-5"
            >
              {data.ctaButtonText ?? `Velg ${data.name}`}
            </Link>
          </span>
        )}
        <div className="text-[18px]">
          <span className="flex flex-col gap-1 text-right">
            <h4 className="font-medium text-[36px] leading-5">{data.price}</h4>
            <p>kr/mnd</p>
          </span>
        </div>
      </div>
    </div>
  );
};
